import { OktonInput, OktonSelect } from '@okton/platform-ui';
import React from 'react';
import {
  Box,
  Heading,
  SimpleGrid,
  Stack,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

const OUTRO_VALOR = '__OUTRO_VALOR__';

export type ValorAtributo = {
  id: string | number;
  nome?: string;
  name?: string;
};

export type AtributoProduto = {
  id: string;
  atributo_externo_id?: string;
  name?: string;
  nome?: string;
  valores?: ValorAtributo[];
  values?: ValorAtributo[];
  value?: string;
  valor_nome?: string;
  value_name?: string;
  valor_externo_id?: string | number | null;
  value_id?: string | number | null;
  usando_outro_valor?: boolean;
  example?: string;
  hint?: string;
  tags?: Record<string, boolean>;
  obrigatorio?: boolean;
  required?: boolean;
  catalog_required?: boolean;
  conditional_required?: boolean;
  fixed?: boolean;
  hidden?: boolean;
  used_hidden?: boolean;
  new_hidden?: boolean;
  relevance?: number;
};

export type AtributoCustomizado = {
  id: string;
  trait: string;
  value: string;
};

type SecaoAtributosProps = {
  atributos: AtributoProduto[];
  setAtributos: React.Dispatch<React.SetStateAction<AtributoProduto[]>>;
  atributosCustomizados: AtributoCustomizado[];
  setAtributosCustomizados: React.Dispatch<
    React.SetStateAction<AtributoCustomizado[]>
  >;
  adicionarAtributoCustomizado: () => void;
  cores: {
    fundoCard: string;
    corBorda: string;
  };
};

export const SecaoAtributos = ({
  atributos,
  setAtributos,
  atributosCustomizados,
  setAtributosCustomizados,
  adicionarAtributoCustomizado,
  cores,
}: SecaoAtributosProps) => {
  const obterValores = (attr: AtributoProduto) =>
    attr.valores || attr.values || [];

  const atualizarAtributoIA = (
    id: string,
    valor: string,
    valorExternoId: string | number | null = null,
    usandoOutroValor = false,
  ) => {
    const novosAtributos = atributos.map(attr =>
      attr.id === id
        ? {
            ...attr,
            value: valor,
            valor_nome: valor,
            value_name: valor,
            valor_externo_id: valorExternoId,
            value_id: valorExternoId,
            usando_outro_valor: usandoOutroValor,
          }
        : attr,
    );

    setAtributos(novosAtributos);
  };

  const selecionarValor = (
    attr: AtributoProduto,
    valorSelecionadoId: string,
  ) => {
    if (valorSelecionadoId === OUTRO_VALOR) {
      atualizarAtributoIA(attr.id, '', null, true);
      return;
    }

    const valores = obterValores(attr);
    const valorSelecionado = valores.find(
      valor => String(valor.id) === valorSelecionadoId,
    );

    atualizarAtributoIA(
      attr.id,
      valorSelecionado?.nome || valorSelecionado?.name || '',
      valorSelecionado?.id || null,
      false,
    );
  };

  const atualizarAtributoCustom = (
    id: string,
    campo: 'trait' | 'value',
    valor: string,
  ) => {
    const novosAtributos = atributosCustomizados.map(attr =>
      attr.id === id ? { ...attr, [campo]: valor } : attr,
    );

    setAtributosCustomizados(novosAtributos);
  };

  const removerAtributoCustom = (id: string) => {
    setAtributosCustomizados(atributosCustomizados.filter(a => a.id !== id));
  };

  return (
    <Stack spacing={6}>
      {atributos.length > 0 && (
        <Box
          bg={cores.fundoCard}
          p={6}
          borderRadius="lg"
          border="1px"
          borderColor={cores.corBorda}
        >
          <Heading
            size="xs"
            mb={4}
            textTransform="uppercase"
            display="flex"
            alignItems="center"
            gap={2}
          >
            Ficha Técnica Sugerida
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {atributos.map(attr => {
              const valores = obterValores(attr);
              const label = attr.name || attr.nome || attr.id;
              const usandoOutroValor = Boolean(attr.usando_outro_valor);

              return (
                <Box key={attr.id}>
                  {valores.length > 0 ? (
                    <Stack spacing={2}>
                      <OktonSelect
                        name={`atributo-${attr.id}`}
                        label={label}
                        size="sm"
                        value={
                          usandoOutroValor
                            ? OUTRO_VALOR
                            : attr.valor_externo_id || attr.value_id || ''
                        }
                        onChange={e => selecionarValor(attr, e.target.value)}
                        placeholder="Selecione"
                      >
                        {valores.map(valor => (
                          <option key={valor.id} value={valor.id}>
                            {valor.nome || valor.name}
                          </option>
                        ))}

                        <option value={OUTRO_VALOR}>Outro valor</option>
                      </OktonSelect>

                      {usandoOutroValor && (
                        <OktonInput
                          name={`atributo-${attr.id}-outro`}
                          size="sm"
                          value={
                            attr.value ||
                            attr.valor_nome ||
                            attr.value_name ||
                            ''
                          }
                          onChange={e =>
                            atualizarAtributoIA(
                              attr.id,
                              e.target.value,
                              null,
                              true,
                            )
                          }
                          placeholder={`Digite ${label}`}
                        />
                      )}
                    </Stack>
                  ) : (
                    <OktonInput
                      name={`atributo-${attr.id}`}
                      label={label}
                      size="sm"
                      value={
                        attr.value || attr.valor_nome || attr.value_name || ''
                      }
                      onChange={e =>
                        atualizarAtributoIA(attr.id, e.target.value)
                      }
                      placeholder={attr.example || attr.hint || label}
                    />
                  )}
                </Box>
              );
            })}
          </SimpleGrid>
        </Box>
      )}

      <Box
        p={6}
        borderRadius="lg"
        border="1px"
        borderColor={cores.corBorda}
        bg={cores.fundoCard}
      >
        <Heading size="sm" fontWeight="bold" mb={6}>
          Atributos Customizados
        </Heading>

        <Stack spacing={3}>
          {atributosCustomizados.map((attr, index) => (
            <Flex key={attr.id} gap={2}>
              <OktonInput
                name={`custom-trait-${attr.id}`}
                placeholder="Ex: Material"
                size="sm"
                value={attr.trait}
                onChange={e =>
                  atualizarAtributoCustom(attr.id, 'trait', e.target.value)
                }
              />

              <OktonInput
                name={`custom-value-${attr.id}`}
                placeholder="Ex: Alumínio"
                size="sm"
                value={attr.value}
                onChange={e =>
                  atualizarAtributoCustom(attr.id, 'value', e.target.value)
                }
              />

              {index === atributosCustomizados.length - 1 ? (
                <IconButton
                  aria-label="Adicionar"
                  icon={<FiPlus />}
                  size="sm"
                  colorScheme="blue"
                  onClick={adicionarAtributoCustomizado}
                  borderRadius="md"
                />
              ) : (
                <IconButton
                  aria-label="Remover"
                  icon={<FiTrash2 />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => removerAtributoCustom(attr.id)}
                  borderRadius="md"
                />
              )}
            </Flex>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};
